import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useMatches } from "react-router-dom";

import { captureFirstTouch } from "@/client/analytics/attribution";
import { registerPage, track } from "@/client/analytics/mixpanel";
import { pageName, resolveSite } from "@/client/analytics/site";
import { trackEvent } from "@/client/analytics/track";

type RouteMatch = { pathname: string; params: Record<string, string | undefined> };

/**
 * Turns a concrete path into the route pattern that produced it, so that every
 * candidate's interview does not become its own distinct "page":
 *
 *   /dashboard/interview/8f2c-41ab  →  /dashboard/interview/:interviewId
 *
 * Without this, grouping page views by URL is meaningless — the ID makes each
 * one unique.
 */
const normalizeRoute = (matches: RouteMatch[]) => {
  const match = matches.at(-1);
  if (!match) return window.location.pathname;

  let route = match.pathname;

  for (const [key, value] of Object.entries(match.params)) {
    if (value) {
      route = route
        .replace(encodeURIComponent(value), `:${key}`)
        .replace(encodeURI(value), `:${key}`)
        .replace(value, `:${key}`);
    }
  }

  // Routes that resolve through a layout come back with a trailing slash while
  // flat ones do not, which would otherwise report /pricing/ and /pricing as two
  // different pages.
  return route.length > 1 ? route.replace(/\/+$/, "") : route;
};

export const AnalyticsTracker = () => {
  const location = useLocation();
  const matches = useMatches() as RouteMatch[];

  const route = useMemo(() => normalizeRoute(matches), [matches]);

  // The page currently being timed, and whether its exit has already been sent.
  // since: when it became visible (null while a tab opened in the background is still hidden).
  const visit = useRef<{ route: string; name: string; since: number | null } | null>(null);
  const exitSent = useRef(false);
  const lastTrackedPath = useRef<string | null>(null);

  /**
   * Closes out the current page with the seconds it was visible. Exactly one per page view: it is
   * sent when the visitor navigates, hides the tab or closes it, whichever comes first. (Until
   * 2026-10-21 every return to the tab re-armed it, so page_exited fired ~2x per page view.)
   */
  const emitExit = useCallback(() => {
    const current = visit.current;
    if (!current || exitSent.current) return;

    exitSent.current = true;
    const visibleMs = current.since === null ? 0 : Date.now() - current.since;

    // eslint-disable-next-line lingui/no-unlocalized-strings -- event name, not copy
    track("page_exited", {
      page_route: current.route,
      page_name: current.name,
      duration_seconds: Math.round(visibleMs / 1000),
    });
  }, []);

  useEffect(() => {
    captureFirstTouch();
    // StrictMode re-runs mount effects for the same page in development. Only
    // the page view itself needs guarding — the timer below is safe to restart,
    // and must be, or the exit for this page would never be sent.
    const name = pageName(route, resolveSite(window.location.hostname));
    if (lastTrackedPath.current !== location.pathname) {
      lastTrackedPath.current = location.pathname;

      emitExit();

      // Before the page view, so it and everything after it carry the page.
      registerPage({ page_route: route, page_name: name });
      trackEvent("page_viewed", { page_route: route, page_name: name, page_title: document.title });
    }

    visit.current = {
      route,
      name,
      since: document.visibilityState === "hidden" ? null : Date.now(),
    };
    exitSent.current = false;
  }, [location.pathname, location.search, route, emitExit]);

  useEffect(() => {
    /**
     * A tab that is closed never fires a route change, so without this the last
     * page of every session would be missing from dwell time — and it is
     * usually the interesting one. Hiding the tab ends the page view: a phone
     * can drop a background tab without any further event.
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") emitExit();
      else if (visit.current && visit.current.since === null) visit.current.since = Date.now();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    // Safari on iOS does not reliably fire visibilitychange when a tab is
    // closed or backgrounded; pagehide is the one it does fire.
    window.addEventListener("pagehide", emitExit);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", emitExit);
      // Deliberately no emitExit() here. This component only unmounts when the
      // whole app tears down, which pagehide/visibilitychange already cover —
      // and calling it would emit a spurious zero-second exit on every
      // StrictMode remount in development.
    };
  }, [emitExit]);

  return null; // This component doesn't render anything
};
